import React from 'react';
import {
  Box,
  Pagination,
  Typography,
  Button,
  Autocomplete,
  Paper,
  InputBase,
  IconButton,
  Divider,
  Popper,
  PopperProps,
} from '@mui/material';
import {
  Sort as SortIcon,
  North as AscSortIcon,
  South as DescSortIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { zoullooColors } from '../../../theme';

export interface SortOption {
  label: string;
  value: string;
  order?: number;
}

export interface SortConfig {
  sortField?: string;
  sortDirection?: 'Asc' | 'Desc';
}

interface TablePaginationProps {
  // Pagination props
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
  
  // Optional sort props
  sortOptions?: SortOption[];
  currentSort?: SortConfig;
  onSortChange?: (sort: SortConfig) => void;
  
  // UI props
  disabled?: boolean;
  sticky?: boolean;
  mx?: any; // Material-UI responsive margin
}

// Custom Popper for sort dropdown
const SortPopper = (props: PopperProps) => (
  <Popper
    {...props}
    placement="bottom-start"
    modifiers={[
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ]}
  />
);

const TablePagination: React.FC<TablePaginationProps> = ({
  currentPage,
  pageSize,
  totalCount,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50],
  sortOptions,
  currentSort,
  onSortChange,
  disabled = false,
  sticky = true,
  mx = { xs: 2, sm: 4, md: 6 },
}) => {
  const { t } = useTranslation();

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    onPageChange(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    onPageSizeChange(newPageSize);
    onPageChange(1); // Reset to first page
  };

  const handleSortFieldChange = (_: any, newValue: SortOption | null) => {
    if (onSortChange) {
      onSortChange({
        sortField: newValue?.value,
        sortDirection: currentSort?.sortDirection || 'Asc',
      });
      onPageChange(1); // Reset to first page
    }
  };

  const handleSortDirectionToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (onSortChange && currentSort?.sortField) {
      onSortChange({
        ...currentSort,
        sortDirection: currentSort.sortDirection === 'Asc' ? 'Desc' : 'Asc',
      });
    }
  };

  const containerStyle = sticky
    ? {
        position: 'sticky' as const,
        bottom: 0,
        bgcolor: 'background.default',
        zIndex: 1,
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        margin: 'auto',
        mx,
        py: '11px',
      }
    : {
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        mt: 2,
        mx,
        py: '11px',
      };

  // Calculate display range
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={containerStyle}
    >
      {/* Page Size Selector */}
      <Box alignSelf="center">
        {pageSizeOptions.map((option) => (
          <Button
            disableRipple
            key={option}
            color="secondary"
            onClick={() => handlePageSizeChange(option)}
            variant={pageSize === option ? 'contained' : 'outlined'}
            sx={{ mr: 1, p: 0, minWidth: 50, height: 32 }}
            disabled={disabled}
          >
            {option}
          </Button>
        ))}
        <Typography variant="body2" component="span" ml={1}>
          {t('common.itemsPerPage')}
        </Typography>
      </Box>

      {/* Pagination Controls */}
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" component="span" ml={1}>
          {startItem}-{endItem}/{totalCount}
        </Typography>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          variant="outlined"
          disabled={disabled}
          sx={{
            '& .MuiPaginationItem-root.Mui-selected': {
              backgroundColor: zoullooColors.secondary.main,
              color: zoullooColors.text.white,
              boxShadow: '0 2px 8px #4836bf48',
              '&:hover': {
                backgroundColor: zoullooColors.secondary.dark,
              },
            },
          }}
        />
      </Box>

      {/* Optional Sort Section */}
      {sortOptions && onSortChange ? (
        <Box display="flex" alignItems="center" gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Autocomplete
              size="small"
              value={sortOptions.find((option) => option.value === currentSort?.sortField) || null}
              onChange={handleSortFieldChange}
              options={sortOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              disabled={disabled}
              slots={{
                popper: SortPopper,
              }}
              sx={{ width: 330 }}
              renderInput={(params) => (
                <Paper
                  sx={{
                    p: '2px 4px',
                    display: 'flex',
                    alignItems: 'center',
                    width: 330,
                    border: `1px solid ${zoullooColors.secondary.light}`,
                    '& .MuiInputBase-root': {
                      border: 'none',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '& .MuiInputBase-adornedEnd': {
                      padding: '0 !important',
                      mr: '0 !important',
                    },
                  }}
                >
                  <SortIcon
                    fontSize="medium"
                    sx={{ color: zoullooColors.secondary.main, ml: 1.3 }}
                  />
                  <InputBase
                    {...params.InputProps}
                    sx={{
                      mx: 2,
                      flex: 1,
                      '& input': {
                        padding: 0,
                      },
                    }}
                    placeholder={t('common.sortBy')}
                    inputProps={{
                      ...params.inputProps,
                      style: { padding: 0 },
                    }}
                  />
                  <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
                  <IconButton
                    disableRipple
                    size="medium"
                    onClick={handleSortDirectionToggle}
                    color="secondary"
                    disabled={!currentSort?.sortField || disabled}
                  >
                    {currentSort?.sortDirection === 'Asc' ? <AscSortIcon /> : <DescSortIcon />}
                  </IconButton>
                </Paper>
              )}
            />
          </Box>
        </Box>
      ) : (
        <Box /> // Empty box to maintain layout when sort is not enabled
      )}
    </Box>
  );
};

export default TablePagination;